import React, { useEffect, useMemo, useState } from 'react'
import Logo from 'presentation/shared/components/Logo'
import { observer } from 'mobx-react'
import { useHistory } from 'react-router'
import { startCase } from 'lodash'
import { Profile } from 'common/enum/profile'
import * as S from './styles'
import SelectField from 'presentation/shared/components/SelectField'
import { useStores } from 'presentation/hooks/use-stores'
import ProfilePic from 'presentation/shared/components/ProfilePic'
import { Link } from 'react-router-dom'
import { formatProfilePicLink } from 'presentation/utils/format-profile-pic-link'
import { toast } from 'react-toastify'
import LoadingModal from 'presentation/shared/components/LoadingModal'
import { User } from 'domain/entities/user-model'
import translateRole from 'common/utils/getTranslatedRole'
import MenuIcon from '@material-ui/icons/Menu'
import notificationStore from 'presentation/contexts/notifications/notification-context'
import { Notifications } from 'presentation/shared/components/Notifications'
import { useServices } from 'presentation/hooks/use-services'
import { useSearchParams } from 'presentation/hooks/use-search-params'

type Props = {
  hideButton?: boolean
  headerButtonTitle?: string
  onClickHeaderButton?: () => void
  onHospitalChange?: () => void
  onClickMenuIcon?: () => void
}

const HospitalHeader = observer(
  ({
    hideButton,
    onClickHeaderButton,
    headerButtonTitle,
    onHospitalChange,
    onClickMenuIcon
  }: Props) => {
    const [user, setUser] = useState({} as User)
    const history = useHistory()
    const searchParams = useSearchParams()
    const currentHospital = useStores().currentHospital
    const [showLoadingModal, setShowLoadingModal] = useState<boolean>(false)
    const currentAccount = useStores().currentAccount.getCurrentAccount()
    const [groupsCanSeeNotifications, setGroupsCanSeeNotifications] =
      useState<boolean>(false)
    const [roleCanSeeNotifications, setRoleCanSeeNotifications] =
      useState<boolean>(false)
    const [queryHospital] = useState<string | number | null>(
      searchParams.get('hospitalId')
    )

    const loadHospitalsList = async () => {
      if (
        user.role === Profile.DIRECTOR_DOCTOR_REGISTER ||
        user.role === Profile.DOCTOR_REGISTER ||
        user?.groups?.includes(Profile.DIRECTOR_DOCTOR_REGISTER) ||
        user?.groups?.includes(Profile.DOCTOR_REGISTER)
      )
        return
      await currentHospital.load()
    }

    const setDefaultActualHospital = async () => {
      if (queryHospital) {
        await currentHospital.setActualHospital(Number(queryHospital))
        searchParams.delete('hospitalId')
      } else if (
        currentHospital.hospitalList.length > 0 &&
        !currentHospital.getActualHospital()
      ) {
        const firstHospital = currentHospital.hospitalList[0]
        await currentHospital.setActualHospital(firstHospital.hospital_id)
      }
    }

    useEffect(() => {
      async function init() {
        await loadHospitalsList()
        await setDefaultActualHospital()
      }
      if (currentAccount) {
        setUser(currentAccount.user)
      }
      init()
    }, [])

    useEffect(() => {
      const profilesCanSeeNotifications = [
        Profile.CRMO,
        Profile.DOCTOR,
        Profile.PATIENT,
        Profile.SECRETARY
      ]
      setGroupsCanSeeNotifications(
        profilesCanSeeNotifications.some((group) =>
          user.groups?.includes(group)
        )
      )
      setRoleCanSeeNotifications(
        profilesCanSeeNotifications.includes(user.role)
      )
    }, [user])

    const hospital = currentHospital.actualHospital
    const hospitalName = hospital?.name

    useEffect(() => {
      if (onHospitalChange) {
        onHospitalChange()
      }
    }, [hospitalName])

    const hospitalOptions = useMemo(() => {
      if (
        user.role === Profile.DIRECTOR_DOCTOR_REGISTER ||
        user.role === Profile.DOCTOR_REGISTER ||
        user?.groups?.includes(Profile.DIRECTOR_DOCTOR_REGISTER) ||
        user?.groups?.includes(Profile.DOCTOR_REGISTER)
      ) {
        return [
          {
            label: 'Mater Dei - Geral',
            value: ''
          }
        ]
      }

      const hospitals = currentHospital.hospitalList.map(
        ({ name, friendly_name, hospital_id }) => ({
          label: friendly_name || name,
          value: hospital_id
        })
      )

      return hospitals
    }, [currentHospital.hospitalList])

    const [, setNotifications] = useState<any>([])
    const notificationService = useServices().notification

    const loadNotifications = async (hospital_id?: number) => {
      if (
        user.groups?.includes(Profile.SECRETARY) ||
        user.role === Profile.SECRETARY ||
        user.groups?.includes(Profile.CRMO) ||
        user.role === Profile.CRMO
      ) {
        const notifications = await notificationService.loadNotifications({
          user_id: user.user_id,
          hospital_id: hospital_id
        })

        setNotifications(
          notifications?.filter((notification) => notification.active === true)
        )
      } else {
        const notifications = await notificationService.loadNotifications({
          user_id: user.user_id
        })

        setNotifications(
          notifications?.filter((notification) => notification.active === true)
        )
      }
    }

    const changeHospital = async (hospitalId: number) => {
      try {
        setShowLoadingModal(true)
        await currentHospital.setActualHospital(hospitalId)
        loadNotifications(hospitalId)
      } catch (error) {
        toast.error('Ocorreu um erro ao tentar trocar para esta unidade!')
      } finally {
        setShowLoadingModal(false)
      }
    }

    const handleChangeHospital = (
      evt: React.ChangeEvent<HTMLSelectElement>
    ) => {
      const value = evt.target.value
      changeHospital(Number(value))
    }

    return (
      <S.Wrapper>
        <S.IconWrapper>
          <MenuIcon onClick={onClickMenuIcon} />
        </S.IconWrapper>
        <S.LogoWrapper>
          <Logo size="normal" />
        </S.LogoWrapper>
        <S.Content>
          <SelectField
            items={hospitalOptions}
            onChange={handleChangeHospital}
            value={currentHospital?.getActualHospital()?.hospital_id.toString()}
          />
          <S.Options>
            {(user?.groups?.includes(Profile.SECRETARY) ||
              user.role === Profile.SECRETARY) &&
              !hideButton && (
                <S.NewSurgicalOrder
                  onClick={() => history.push('/pedido/novo')}
                  data-testid="new-surgical-order-button"
                >
                  <span>Novo pedido cirúrgico</span>
                </S.NewSurgicalOrder>
              )}
          </S.Options>

          {onClickHeaderButton && headerButtonTitle ? (
            <S.HeaderButton onClick={() => onClickHeaderButton()}>
              <span>{headerButtonTitle}</span>
            </S.HeaderButton>
          ) : null}

          {(groupsCanSeeNotifications || roleCanSeeNotifications) && (
            <Notifications
              currentHospitalId={currentHospital
                ?.getActualHospital()
                ?.hospital_id.toString()}
            />
          )}

          <S.UserWrapper>
            <S.UserInfo>
              <strong>{startCase(user.name?.toLocaleLowerCase())}</strong>
              <label>
                {user.isMainRole
                  ? translateRole(user.isMainRole)?.toUpperCase()
                  : translateRole(user.role)?.toUpperCase()}
              </label>
            </S.UserInfo>
            {user?.groups?.includes(Profile.SECRETARY) ||
            user.role === Profile.SECRETARY ? (
              <Link to="/perfil/0101">
                <ProfilePic
                  data-testid="profile pic"
                  size="small"
                  src={
                    user.photoKey &&
                    formatProfilePicLink(user.photoKey, user.user_id)
                  }
                />
              </Link>
            ) : (
              <Link to="/perfil/0101">
                <ProfilePic data-testid="profile pic" size="small" />
              </Link>
            )}
          </S.UserWrapper>
        </S.Content>
        <LoadingModal
          show={showLoadingModal || notificationStore.isLoadingPendency}
        />
      </S.Wrapper>
    )
  }
)

export default HospitalHeader
